import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBell, FaCheckDouble, FaPlaneDeparture, FaExclamationTriangle,
  FaClipboardList, FaCog, FaCheckCircle,
} from 'react-icons/fa';
import api, { errorMessage } from '../api';
import {
  PageShell, Card, Button, Alert, Loading, EmptyState, formatDateTime,
} from './attendance/ui';

const CATEGORY_ICON = {
  leave_request: <FaPlaneDeparture />,
  leave_decision: <FaCheckCircle />,
  attendance: <FaExclamationTriangle />,
  missing_attendance: <FaExclamationTriangle />,
  submission: <FaClipboardList />,
  system: <FaCog />,
};

const LEVEL_TONE = {
  info: 'bg-blue-50 text-blue-600',
  success: 'bg-green-50 text-green-600',
  warning: 'bg-amber-50 text-amber-600',
  error: 'bg-red-50 text-red-600',
};

/** Leave decisions, attendance issues and administrative notices. */
const NotificationsPage = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const { data } = await api.get('notifications/',
        { params: filter === 'unread' ? { is_read: false } : {} });
      setItems(data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load notifications.'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (notification) => {
    if (notification.isRead) return;
    try {
      await api.post(`notifications/${notification.id}/read/`);
      setItems((current) => current.map((item) =>
        item.id === notification.id ? { ...item, isRead: true } : item));
    } catch (err) {
      setError(errorMessage(err, 'Could not update the notification.'));
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('notifications/read-all/');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not mark everything as read.'));
    }
  };

  const unread = items.filter((item) => !item.isRead).length;

  return (
    <PageShell
      title="Notifications"
      subtitle={unread ? `${unread} unread` : 'You are all caught up'}
      actions={unread > 0 && (
        <Button variant="secondary" onClick={markAllRead}>
          <FaCheckDouble /> Mark all read
        </Button>
      )}
    >
      <Alert>{error}</Alert>

      <div className="flex gap-2 mb-5">
        {[['all', 'All'], ['unread', 'Unread']].map(([value, label]) => (
          <button key={value} onClick={() => setFilter(value)}
            className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${filter === value ? 'bg-[#15A857] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div>

      <Card>
        {loading ? <Loading /> : items.length === 0 ? (
          <EmptyState icon={<FaBell />} title="No notifications"
            hint="Leave decisions, attendance issues and administrative notices land here." />
        ) : (
          <div className="space-y-2">
            {items.map((notification) => {
              const body = (
                <div className={`flex items-start gap-4 p-4 rounded-xl transition-colors ${notification.isRead ? 'bg-white hover:bg-gray-50' : 'bg-[#15A857]/5 hover:bg-[#15A857]/10'}`}>
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${LEVEL_TONE[notification.level] || LEVEL_TONE.info}`}>
                    {CATEGORY_ICON[notification.category] || <FaBell />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <span className={`font-semibold ${notification.isRead ? 'text-gray-700' : 'text-gray-900'}`}>
                        {notification.title}
                      </span>
                      {!notification.isRead && (
                        <span className="w-2 h-2 rounded-full bg-[#15A857] flex-shrink-0 mt-2" />
                      )}
                    </div>
                    {notification.message && (
                      <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    )}
                    <div className="text-xs text-gray-400 mt-1.5">
                      {formatDateTime(notification.createdAt)}
                    </div>
                  </div>
                </div>
              );

              return notification.link ? (
                <Link key={notification.id} to={notification.link} onClick={() => markRead(notification)}
                  className="block">{body}</Link>
              ) : (
                <button key={notification.id} onClick={() => markRead(notification)}
                  className="block w-full text-left">{body}</button>
              );
            })}
          </div>
        )}
      </Card>
    </PageShell>
  );
};

export default NotificationsPage;
